/**
 * useSolidMeshUnitDisplay - SolidMesh 専用の単位変換境界
 *
 * SolidMesh の内部値・保存値・viewer 計算値は常に m 基準で、この composable は出力境界
 * でのみ現場単位へ変換する。換算係数は自前で定義せず UnitConverter から導出する。
 */
import { computed } from "vue";
import { useUnitDisplay } from "@/composables/useUnitDisplay";
import { type DistanceUnit, distanceUnits } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import type {
	SolidMeshExportGeometry,
	SolidMeshLengthUnit,
	SolidMeshUnitTransform,
	SolidMeshViewerGeometry,
} from "./solidMeshUnit.types";

/** Metres per base unit. SI prefix only, unrelated to the site distance unit. */
const METERS_PER_BASE_UNIT = {
	m: 1,
	mm: 0.001,
} as const;

/** Round for display only. Undefined decimals keeps the full precision. */
function roundForDisplay(value: number, decimals?: number): number {
	return decimals === undefined ? value : Number(value.toFixed(decimals));
}

/**
 * SolidMesh の単位変換境界を返す。
 * @returns 現場距離単位と、出力用 geometry 変換
 */
export function useSolidMeshUnitDisplay(): SolidMeshUnitTransform {
	// Site units come from the shared boundary composable so the store read and the
	// DEFAULT_SITE_UNITS fallback are not duplicated here.
	const { siteUnits, distanceLabel } = useUnitDisplay();

	const distanceUnit = computed<DistanceUnit>(() => siteUnits.value.distance);

	const isMetricDistance = computed(
		() => distanceUnit.value === distanceUnits.METERS,
	);

	// The numeric port is used directly: useUnitDisplay's toDisplayDistance returns a
	// formatted result object per call, which is unsuited to per-vertex conversion.
	const distanceUnitPort = computed(() =>
		createDistanceUnitPort(distanceUnit.value),
	);

	/**
	 * 内部 m の geometry を現場単位へ変換し、単位定義に必要な情報を添えて返す。
	 * `metersPerUnit` は UnitConverter の換算が乗算のみで内部単位が m であることから、
	 * 表示単位の 1 を m へ変換した値が換算係数そのものになる性質を使って導出する。
	 */
	function toExportGeometry(
		geometry: SolidMeshViewerGeometry,
	): SolidMeshExportGeometry {
		const port = distanceUnitPort.value;
		return {
			vertices: geometry.vertices.map((meters) => port.toDisplay(meters)),
			indices: [...geometry.indices],
			distanceUnit: distanceUnit.value,
			metersPerUnit: port.toMeters(1),
		};
	}

	/** Value in the given unit to internal metres. */
	function toMeters(value: number, unit: SolidMeshLengthUnit): number {
		return unit === "site"
			? distanceUnitPort.value.toMeters(value)
			: value * METERS_PER_BASE_UNIT[unit];
	}

	/** Internal metres to the given unit. */
	function fromMeters(meters: number, unit: SolidMeshLengthUnit): number {
		return unit === "site"
			? distanceUnitPort.value.toDisplay(meters)
			: meters / METERS_PER_BASE_UNIT[unit];
	}

	/**
	 * Converts a length between any two units via metres. The path does not depend
	 * on the site distance unit, so callers never branch on it.
	 */
	function convertLength(
		value: number,
		from: SolidMeshLengthUnit,
		to: SolidMeshLengthUnit,
		decimals?: number,
	): number {
		if (from === to) return roundForDisplay(value, decimals);
		return roundForDisplay(fromMeters(toMeters(value, from), to), decimals);
	}

	return {
		distanceUnit,
		distanceLabel,
		isMetricDistance,
		convertLength,
		toExportGeometry,
	};
}
